"use client"

import { useCallback, useMemo } from "react"
import { useTranslations } from "@/lib/intl/use-translations"
import { useLocale } from "next-intl"
import { useCountry } from "@/components/layout/country-provider"
import type { NavbarItem } from "@/components/ui/navbar"
import type { FooterColumn } from "@/components/ui/footer"
import { bookingUrlForCountry } from "@/lib/booking"
import { blobMediaUrl } from "@/lib/blob-url"
import { isAllowedInCountry, type GateId } from "@/lib/country-gates"
import { localizedPath } from "@/lib/localized-route-slugs"
import { isBookingOpen, isComingSoon, isWaitlistOnly } from "@/i18n/config"
import { useWaitlist } from "@/components/ui/waitlist-context"

/** Country-specific SEO landing pages exposed via the footer. Each entry
 *  pairs a footer translation key with the page's slug and the country-gate
 *  that governs the page — the link renders only where that page actually
 *  exists, so it can never point at a 404 (e.g. on NL/DE). */
type FooterLandingPage = {
  labelKey: string
  path: string
  gate: GateId
}

const FOOTER_LANDING_PAGES: readonly FooterLandingPage[] = [
  {
    labelKey: "fullBodyMriZurich",
    path: "/full-body-mri-zurich",
    // Zurich-specific SEO page — CH only (page:full-body-mri-zurich).
    gate: "page:full-body-mri-zurich",
  },
] as const

export function useLocalePath() {
  const locale = useLocale()
  const { code: country } = useCountry()
  return useCallback(
    (path: string) => {
      if (
        path.startsWith("http") ||
        path.startsWith("#") ||
        path.startsWith("tel:") ||
        path.startsWith("mailto:")
      )
        return path
      const bare = path.startsWith("/") ? path : `/${path}`
      return `/${country}/${locale}${localizedPath(bare, locale)}`
    },
    [locale, country]
  )
}

/** The `/{country}/{locale}` prefix for the current request, with no
 *  trailing slash. Pass to `sanitizeHtml()` to rewrite bare internal
 *  links in CMS-authored content. */
export function useLocalePrefix(): string {
  const locale = useLocale()
  const { code: country } = useCountry()
  return `/${country}/${locale}`
}

export function useLocalizedNavigation({
  membershipFirst = false,
  navHiddenPaths = [],
}: {
  /** Membership markets (CH + AT): the "How it works" mega-menu leads with an
   *  Ahead-membership card and the standalone Membership nav item is dropped on
   *  desktop. See `feature:membership-first-positioning` in lib/country-gates.ts. */
  membershipFirst?: boolean
  /** Bare `/tests/<slug>` paths hidden from the core nav for this country
   *  (CMS `products.marketStatus[].hideFromNav`). Filters the "What we offer"
   *  children; the hardcoded add-ons are filtered in the menu components. */
  navHiddenPaths?: string[]
} = {}) {
  const t = useTranslations("nav")
  const ft = useTranslations("footer")
  const ut = useTranslations("ui")
  const locale = useLocale()
  const { code: country } = useCountry()
  const localePath = useLocalePath()
  const { openWaitlist } = useWaitlist()
  // Pre-launch markets (NL today) route the booking CTA to the waitlist modal
  // instead of the external booking flow — mirrors the navbar CTA in PageShell.
  const isWaitlist = isWaitlistOnly(country)

  // CH-only nav-item delta (shipped with the marketing-navbar-v3 launch):
  // Membership + Insurance primary slots, Pricing/Contact-us hidden on
  // desktop, "What we offer" → "Check-ups". NL/DE pre-launch markets keep
  // the simpler nav.
  const applyNavItemDelta = country === "ch"
  // NL nav delta: drop Pricing/FAQ/Contact (no reimbursement or support-desk
  // positioning pre-launch), add a Membership pill (no Insurance — not offered
  // there yet) and a Reviews link. Keeps the NL bar aligned with CH minus the
  // Swiss-only surfaces.
  const isNl = country === "nl"
  // NL + AT pre-launch nav delta: drop Pricing/FAQ/Contact (no pricing or
  // support-desk positioning pre-launch) and add a Reviews link. The
  // Membership pill stays NL-only — AT follows DE's simpler nav.
  const isPreLaunchMarket = isNl || country === "at"
  // DE runs a lean nav/footer pre-MRI-launch (no FAQ link, no imaging add-ons).
  const isDe = country === "de"

  const navItems = useMemo<NavbarItem[]>(
    () => [
      {
        // Trigger label stays "How it works"; in membership markets the dropdown
        // leads with an Ahead-membership card (and the standalone Membership nav
        // item is dropped — it lives here instead).
        label: t("howItWorks"),
        href: localePath("/how-it-works"),
        megaMenu: true,
        megaMenuType: "howItWorks" as const,
        children: [
          ...(membershipFirst
            ? [
                {
                  label: t("membership"),
                  description: t("mfMembershipMenuDesc"),
                  href: localePath("/membership"),
                  imageSrc: blobMediaUrl(
                    "/images/homepage-membership/menu-card.png"
                  ),
                },
              ]
            : []),
          {
            label: t("howItWorks"),
            description: t("howItWorksDesc"),
            href: localePath("/how-it-works"),
            imageSrc: blobMediaUrl("/images/mega-menu/how-it-works.jpg"),
          },
          {
            label: t("healthRisks"),
            description: t("healthRisksDesc"),
            href: localePath("/what-we-detect"),
            imageSrc: blobMediaUrl("/images/mega-menu/health-risks.jpg"),
          },
          {
            label: t("yourHealthReport"),
            description: t("healthReportDesc"),
            href: localePath("/report"),
            imageSrc: blobMediaUrl("/images/mega-menu/health-report.jpg"),
          },
          ...(isAllowedInCountry("page:biomarkers", country)
            ? [
                {
                  label: t("biomarkersWeCheck"),
                  description: t("biomarkersDesc"),
                  href: localePath("/biomarkers"),
                  imageSrc: blobMediaUrl("/images/mega-menu/biomarkers.jpg"),
                },
              ]
            : []),
        ],
      },
      {
        // CH renames the mega-menu trigger from "What we offer" →
        // "Check-ups" (and locale equivalents); NL/DE keep the original.
        label: applyNavItemDelta ? t("checkups") : t("whatWeOffer"),
        href: localePath("/tests"),
        megaMenu: true,
        megaMenuType: "whatWeOffer" as const,
        // Drop the featured services flagged hide-from-nav for this country
        // (CMS `hideFromNav`). The hardcoded add-ons are filtered in the menu
        // components against the same `navHiddenPaths`.
        children: [
          ...(navHiddenPaths.includes("/tests/full-body-mri")
            ? []
            : [
                {
                  label: t("fullBodyMri"),
                  href: localePath("/tests/full-body-mri"),
                },
              ]),
          ...(navHiddenPaths.includes("/tests/advanced-blood-test")
            ? []
            : [
                {
                  label: t("advancedBloodTest"),
                  href: localePath("/tests/advanced-blood-test"),
                },
              ]),
        ],
      },
      // Pricing — hidden entirely on pre-launch markets (NL, AT). CH keeps it
      // but drops it from the desktop bar (Membership + Insurance share the
      // primary slots); mobile slide-out still shows it.
      ...(isPreLaunchMarket
        ? []
        : [
            {
              label: t("pricing"),
              href: localePath("/pricing"),
              ...(applyNavItemDelta ? { hideOnDesktop: true } : {}),
            },
          ]),
      // CH inserts Membership + Insurance between Pricing and Locations. NL
      // adds Membership only (Insurance isn't offered there yet). DE keeps the
      // simpler nav (no Membership pill; still reachable from the mobile menu's
      // hardcoded account block and the footer booking column).
      // In membership markets the standalone Membership item is dropped on
      // desktop — membership leads the "How it works" mega-menu instead. It's a
      // plain conditional now that `membershipFirst` is country-derived (known
      // at render on both server and client, so there's nothing to flash). Kept
      // on mobile via the account block (the mega-menu isn't reachable there).
      ...(applyNavItemDelta
        ? [
            ...(membershipFirst
              ? []
              : [
                  {
                    label: t("membershipShort"),
                    href: localePath("/membership"),
                    // The mobile slide-out already links Membership from its
                    // account block (with the NEW badge) — hide the nav item
                    // there so it doesn't appear twice (AHD-6728).
                    hideOnMobile: true,
                  },
                ]),
            { label: t("insurance"), href: localePath("/insurance") },
          ]
        : // Membership isn't offered in NL — no standalone Membership nav item
          // there (AHD-6910). Other pre-launch markets never had one.
          []),
      { label: t("locations"), href: localePath("/locations") },
      // FAQ — hidden entirely on pre-launch markets (NL, AT) and on DE (the
      // FAQ page is country-gated there); on the desktop bar everywhere else.
      ...(isPreLaunchMarket || isDe
        ? []
        : [
            {
              label: t("faq"),
              href: localePath("/faq"),
            },
          ]),
      // Reviews — pre-launch markets (NL, AT) only. Kept out of the CH primary
      // nav: the navbar-v4 test showed the Reviews slot drew about half the
      // traffic of the FAQ slot it would have replaced, and the bundle hurt
      // desktop conversion.
      ...(isPreLaunchMarket
        ? [{ label: t("reviews"), href: localePath("/reviews") }]
        : []),
      // Contact us — hidden on NL during its waitlist phase. AT keeps it: the
      // trimmed Austrian launch set (2026-08-28) includes /contact, so the nav
      // has to reach it. CH drops it from the desktop bar (room for Insurance)
      // but keeps it in the mobile slide-out.
      ...(isNl
        ? []
        : [
            {
              label: ut("contactUs"),
              href: localePath("/contact"),
              ...(applyNavItemDelta ? { hideOnDesktop: true } : {}),
            },
          ]),
    ],
    [
      t,
      ut,
      localePath,
      applyNavItemDelta,
      isPreLaunchMarket,
      isNl,
      isDe,
      country,
      membershipFirst,
      navHiddenPaths,
    ]
  )

  const footerColumns = useMemo<FooterColumn[]>(() => {
    // DE launches as a lean footer: the imaging add-ons, membership, careers,
    // ambassador programme and several info pages aren't live there yet.
    const columns: FooterColumn[] = [
      {
        title: ft("information"),
        links: [
          { label: ft("howItWorks"), href: localePath("/how-it-works") },
          { label: ft("healthRisks"), href: localePath("/what-we-detect") },
          ...(isAllowedInCountry("page:biomarkers", country)
            ? [{ label: ft("biomarkers"), href: localePath("/biomarkers") }]
            : []),
          ...(isAllowedInCountry("page:pricing", country)
            ? [{ label: ft("pricing"), href: localePath("/pricing") }]
            : []),
          // DE pre-launch: trim the footer to the live surfaces — Wissen
          // (insights), FAQ, Kostenübernahme and "Seite durchsuchen" are
          // hidden until those pages are ready for the German market.
          ...(!isDe && isAllowedInCountry("page:insights", country)
            ? [{ label: ft("insights"), href: localePath("/insights") }]
            : []),
          ...(!isDe && isAllowedInCountry("page:faq", country)
            ? [{ label: ft("faq"), href: localePath("/faq") }]
            : []),
          // Reimbursement link: CH → the /insurance checker. DE hides its
          // Kostenübernahme page pre-launch; NL has no positioning yet.
          ...(!isDe && isAllowedInCountry("page:insurance", country)
            ? [
                {
                  label: ft("insuranceCoverage"),
                  href: localePath("/insurance"),
                },
              ]
            : []),
          // CH-only: the comparison page is Switzerland-specific (AHD-6411).
          ...(country === "ch"
            ? [
                {
                  label: ft("howAheadCompares"),
                  href: localePath("/compare-health-check-ups-switzerland"),
                },
              ]
            : []),
          ...(!isDe
            ? [{ label: ft("searchSite"), href: localePath("/search") }]
            : []),
        ],
      },
      {
        title: ft("bookingCheckup"),
        links: [
          // Pre-launch markets (NL) have no booking flow yet — the link opens
          // the waitlist modal AND reads "Join waitlist" (2026-07-29), same
          // label as the navbar CTA.
          isWaitlist
            ? { label: ut("joinWaitlist"), onClick: () => openWaitlist() }
            : {
                label: ft("bookYourCheckup"),
                href: bookingUrlForCountry(country, locale),
              },
          // Discovery-call page is gated out of NL during the waitlist phase.
          ...(isAllowedInCountry("page:free-call", country)
            ? [
                {
                  label: ft("scheduleDiscoveryCall"),
                  href: localePath("/free-call"),
                },
              ]
            : []),
          { label: ft("findNearestClinic"), href: localePath("/locations") },
          ...FOOTER_LANDING_PAGES.filter((page) =>
            isAllowedInCountry(page.gate, country)
          ).map(({ labelKey, path }) => ({
            label: ft(labelKey as Parameters<typeof ft>[0]),
            href: localePath(path),
          })),
        ],
      },
      {
        title: ft("whatWeTest"),
        links: [
          {
            label: ft("fullBodyMri"),
            href: localePath("/tests/full-body-mri"),
            // DE: full-body MRI isn't bookable yet → coming-soon pill.
            ...(isComingSoon(country, "mri")
              ? { badge: ut("comingSoon") }
              : {}),
          },
          {
            label: ft("advancedBloodTest"),
            href: localePath("/tests/advanced-blood-test"),
          },
          // Imaging add-ons are hidden on DE until MRI launches there.
          ...(isDe
            ? []
            : [
                {
                  label: ft("calciumScore"),
                  href: localePath("/tests/calcium-score"),
                },
                {
                  label: ft("bodyComposition"),
                  href: localePath("/tests/body-composition"),
                },
                {
                  label: ft("brainAnalysis"),
                  href: localePath("/tests/brain-volumetry"),
                },
              ]),
        ],
      },
      {
        title: ft("company"),
        links: [
          { label: ft("aboutUs"), href: localePath("/about") },
          // Careers + ambassador programme are hidden on DE pre-launch, and
          // don't exist at all on AT (page:careers / page:ambassadors).
          ...(!isDe && isAllowedInCountry("page:careers", country)
            ? [
                {
                  label: ft("careers"),
                  href: localePath("/careers"),
                  badge: ft("hiringBadge"),
                },
              ]
            : []),
          { label: ft("contactUs"), href: localePath("/contact") },
          ...(!isDe && isAllowedInCountry("page:ambassadors", country)
            ? [
                {
                  label: ft("ambassadorProgram"),
                  href: localePath("/ambassadors"),
                },
              ]
            : []),
          { label: ft("reviews"), href: localePath("/reviews") },
        ],
      },
    ]

    // DEXA is an imaging add-on → hidden on DE (alongside the other add-ons
    // above), live unconditionally elsewhere. Gender-health is intentionally
    // NOT surfaced here: prostate-mri + pelvic-mri replaced it, so the combined
    // page stays routable but hidden from nav (TEST_LISTING_HIDDEN_SLUGS).
    if (!isDe) {
      const whatWeTest = columns.find((c) => c.title === ft("whatWeTest"))
      whatWeTest?.links.splice(3, 0, {
        label: ft("dexaScan"),
        href: localePath("/tests/dexa-scan"),
      })
    }

    // Ahead Membership in the footer only where its booking flow is open (CH).
    // DE (coming-soon) and waitlist NL (not offered) both stay out — a footer
    // link is a plain promise with no pill to qualify it.
    if (isBookingOpen(country, "membership")) {
      const booking = columns.find((c) => c.title === ft("bookingCheckup"))
      booking?.links.unshift({
        label: t("membership"),
        href: localePath("/membership"),
        badge: t("newBadge"),
      })
    }

    return columns
  }, [ft, t, ut, localePath, locale, country, isDe, isWaitlist, openWaitlist])

  const footerLabels = useMemo(
    () => ({
      tagline: ft("tagline"),
      whatsapp: ft("whatsapp"),
      copyright: ft("copyright"),
      legalLinks: [
        // AT still hides T&C: that page 404s there until Austrian counsel
        // delivers (AHD-6893), so linking it would be a dead link. Privacy is
        // linked as of 2026-08-28 — the route renders, and a market that
        // collects waitlist emails has to be able to reach a
        // Datenschutzerklärung. NL's own documents went live in AHD-7917.
        //
        // The link is NOT compliance. Every section body in the de-AT
        // `privacy` namespace is still a `[TODO: legal]` placeholder behind a
        // visible "wird derzeit finalisiert" banner — exactly the stub
        // /imprint already ships on AT. Real content has to land before
        // AT_STATUS leaves `built` and the market goes public.
        ...(country === "at"
          ? []
          : [
              {
                label: ft("termsConditions"),
                href: localePath("/terms-conditions"),
              },
            ]),
        { label: ft("privacyPolicy"), href: localePath("/privacy") },
        { label: ft("imprint"), href: localePath("/imprint") },
      ],
      cookieSettings: ft("cookieSettings"),
    }),
    [ft, localePath, country]
  )

  return { navItems, footerColumns, footerLabels }
}
